import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ContentChild,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { asyncScheduler, fromEvent, Subscription, timer } from 'rxjs';
import { debounceTime, filter, first } from 'rxjs/operators';

import { MediaQuery } from '../../services/media-query.service';
import { AnchorDirective } from './dropdown-anchor.directive';
import { fromWindowEvent } from 'core/utils/rxjs/window-event';
import { WindowService } from 'core/services/window.service';

type DropdownPosition = 'before' | 'after' | 'above' | 'below' | 'center';

const BELOW = {
  originY: 'bottom',
  overlayY: 'top',
  originX: 'start',
  overlayX: 'start',
} as ConnectedPosition;

const ABOVE = {
  originY: 'top',
  overlayY: 'bottom',
  originX: 'start',
  overlayX: 'start',
} as ConnectedPosition;

const AFTER = {
  originY: 'center',
  overlayY: 'center',
  originX: 'end',
  overlayX: 'start',
} as ConnectedPosition;

const BEFORE = {
  originY: 'center',
  overlayY: 'center',
  originX: 'start',
  overlayX: 'end',
} as ConnectedPosition;

@Directive({})
export class DropdownBase implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private static readonly dropdownList: DropdownBase[] = [];

  static get isAnyOpened(): boolean {
    return this.dropdownList.filter(d => d.isActive()).length > 0;
  }

  @ViewChild(CdkConnectedOverlay, { static: true })
  cdkOverlay!: CdkConnectedOverlay;

  @Input()
  anchor?: AnchorDirective;

  @Input()
  input?: AnchorDirective;

  @Input()
  theme: 'l' | 'd' = 'd';

  @Input()
  openDelay = 0;

  @Input()
  showOverlay?: boolean;

  @Input()
  set active(a: boolean) {
    this.isActive.set(a);
  }

  get active(): boolean {
    return this.isActive();
  }

  get anchorRef() {
    return this.anchor || this._anchorRef;
  }

  @Input()
  closeOnWindowBlur = true;

  @Input()
  overlayClass = 'dropdown';

  @Input()
  mobileAppearance: 'custom' | 'screen' | 'inset-top' | 'drawer' | 'modal' = 'drawer';

  @Input()
  position: DropdownPosition = 'below';

  @Input()
  customPosition?: ConnectedPosition;

  @Input()
  set overlayOrigin(o: ElementRef<HTMLElement> | HTMLElement) {
    this.customOverlayOrigin = o;
  }

  get overlayOrigin() {
    return this.customOverlayOrigin || this.anchor?.element || this.elementRef;
  }

  @Input()
  /** close on click inside dropdown container
   * default: false - so it will close only on click outside
   */
  innerClickClose?: boolean;

  @Input()
  useHover?: boolean;

  @Input()
  /** any css valid width or number in px if not defined - width will be same as overlayOrigin width */
  width: number | string = '';

  @Input()
  overlayPush = false;

  @Output()
  opened = new EventEmitter<void>();

  @Output()
  beforeOpen = new EventEmitter<void>();

  @Output()
  closed = new EventEmitter<void>();

  enableAnimation = true;
  isEnabled = true;
  result?: 'OK' | 'CANCEL' | 'DELETE';

  protected transitionState?: string;
  protected overlayPosition: ConnectedPosition[] = [BELOW, ABOVE];
  protected destroyRef = inject(DestroyRef);
  protected isActive = signal(false);
  protected originWidth = 0;
  protected customOverlayOrigin?: ElementRef<HTMLElement> | HTMLElement;
  private mouseUpSub = new Subscription();
  private preventClosing = false;
  private isFocused = false;

  // for now support only static button anchor
  @ContentChild(AnchorDirective, { static: true })
  private _anchorRef?: AnchorDirective;

  protected window = inject(WindowService);

  constructor(
    protected media: MediaQuery,
    protected renderer: Renderer2,
    protected router: Router,
    public elementRef: ElementRef<HTMLElement>,
    protected document: Document,
  ) {
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scheduleClose({ closeAll: true });
      });
  }

  ngOnDestroy(): void {
    const foundIndex = DropdownBase.dropdownList.findIndex(d => d === this);
    if (foundIndex >= 0) {
      DropdownBase.dropdownList.splice(foundIndex, 1);
    }
  }

  ngOnInit(): void {
    const elementWidth =
      (this.overlayOrigin as ElementRef<HTMLElement>).nativeElement || (this.overlayOrigin as HTMLElement);
    this.window.requestAnimationFrame(() => {
      this.originWidth = elementWidth.clientWidth;
    });
    this.originWidth = elementWidth.clientWidth;
    fromWindowEvent('resize')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.window.requestAnimationFrame(() => {
          this.originWidth = elementWidth.clientWidth;
        });
      });
    // fromWindowEvent('scroll')
    //   .pipe(takeUntilDestroyed(this.destroyRef))
    //   .subscribe(() => {
    //     if (this.media.is('medium')) {
    //       this.window.requestAnimationFrame(() => {
    //         this.scheduleClose({ closeAll: true });
    //       });
    //     }
    //   });
    fromWindowEvent('click')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.isFocused && !this.preventClosing && this.isEnabled) {
          this.scheduleClose({ closeAll: true });
        }
      });
    fromWindowEvent('blur')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.media.atLeast('medium') && this.closeOnWindowBlur) {
          this.scheduleClose({ closeAll: true });
        }
      });
    this.media.mediaChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.scheduleClose({ closeAll: true });
    });
    DropdownBase.dropdownList.push(this);
  }

  ngOnChanges(changes: SimpleChanges<DropdownBase>): void {
    if (changes.position || changes.customPosition) {
      this.updatePosition(this.position);
    }
  }

  isNumeric(v: unknown) {
    return Number.isInteger(v);
  }

  updatePosition(position: DropdownPosition): void {
    if (this.customPosition) {
      this.overlayPosition = [this.customPosition];
    } else {
      this.overlayPosition = [BELOW];
      if (position === 'above') {
        this.overlayPosition = [ABOVE, BELOW];
      } else if (position === 'after') {
        this.overlayPosition = [AFTER, BEFORE, ABOVE, BELOW];
      } else if (position == 'before') {
        this.overlayPosition = [BEFORE, AFTER, ABOVE, BELOW];
      } else if (position === 'center') {
        const above = { ...ABOVE };
        const below = { ...BELOW };
        above.originX = below.originX = 'center';
        above.overlayX = below.overlayX = 'center';
        this.overlayPosition = [below, above, BEFORE, AFTER];
      }
    }
  }

  ngAfterViewInit(): void {
    if (!this.anchorRef) {
      return;
    }
    const anchorElement = this.anchorRef.element;
    const anchorHostElement = this.anchorRef.element;
    const currentElement = this.elementRef.nativeElement;
    // if not input - close dropdown on second click at anchor
    if (!(anchorHostElement instanceof HTMLInputElement)) {
      this.renderer.setAttribute(anchorHostElement, 'tabindex', '0');
      fromEvent<MouseEvent>(anchorHostElement, 'mousedown')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(e => {
          if (!this.anchorRef?.anchorEnabled) {
            return;
          }
          if (this.isFocused) {
            asyncScheduler.schedule(() => anchorElement.blur());
          } else {
            anchorElement.focus();
          }
          e.preventDefault();
        });
    }
    fromEvent(anchorElement, 'focus')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isActive() && !this.preventClosing) {
          this.scheduleClose({ closeAll: true });
          return;
        }
        this.isFocused = true;
        if (!this.anchorRef?.anchorEnabled) {
          return;
        }
        this.forceStopClose();
        this.open();
      });
    fromEvent(anchorElement, 'blur')
      .pipe(debounceTime(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isFocused = false;
        if (this.preventClosing) {
          return;
        }
        if (!this.anchorRef?.anchorEnabled || !this.isEnabled) {
          return;
        }
        this.scheduleClose({ closeAll: true });
      });
    if (this.useHover) {
      let mouseLeaveSub = new Subscription();
      let leaveOccurred = false;
      const openDebounceTime = 150;
      fromEvent(currentElement, 'mouseenter')
        .pipe(debounceTime(openDebounceTime), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (leaveOccurred) {
            return;
          }
          this.open();
          console.log(this.cdkOverlay.overlayRef?.overlayElement);
          // close other dropdown immediately instead 250ms debounce
          DropdownBase.dropdownList.forEach(d => (d !== this ? d.close() : null));
          mouseLeaveSub.unsubscribe();
          mouseLeaveSub = fromEvent(currentElement, 'mouseleave')
            .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this.close();
            });
        });
      fromEvent(currentElement, 'mouseleave')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.isActive()) {
            return;
          }
          leaveOccurred = true;
          asyncScheduler.schedule(() => (leaveOccurred = false), openDebounceTime + 50);
        });
    }
  }

  toggle(): void {
    if (this.active) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (!this.isEnabled) {
      return;
    }
    if (this.active) {
      return;
    }
    if (!this.anchorRef) {
      this.stopClose();
      console.log('no chandoo');
    }
    this.beforeOpen.next();
    if (this.openDelay) {
      timer(this.openDelay)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.cdkOverlay.overlayRef?.overlayElement.firstElementChild?.remove();
          this.isActive.set(true);
          this.onOpen();
        });
    } else {
      this.cdkOverlay.overlayRef?.overlayElement.firstElementChild?.remove();
      this.isActive.set(true);
      this.onOpen();
    }
  }

  protected onOpen(): void {
    if (this.anchorRef) {
      this.renderer.addClass(this.anchorRef.element, 'anchor-active');
    }
    if (this.media.is('small only')) {
      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      if (this.overlayClass !== 'full-screen') {
        // if there is another open dropdown exists
        if (DropdownBase.dropdownList.filter(d => d.isActive() && d !== this).length) {
          // TODO: create new overlay inside cdk-overlay-container with z-index greater than first dropdown
        } else if (this.showOverlay) {
          this.renderer.addClass(this.document.body, 'cdk-overlay');
        }
      }
    }
    if (this.enableAnimation) {
      this.transitionState = this.getTransition();
    }
    this.opened.next();
  }

  private getTransition() {
    return this.media.is('small only') ? 'open-mobile' : 'open';
  }

  close(): void {
    if (!this.active) {
      return;
    }
    if (this.window.getItem('_dropdown_debug')) {
      return;
    }
    this.isActive.set(false);
    this.anchorRef?.element.blur();
    this.onClose();
  }

  protected onClose(): void {
    if (this.anchorRef) {
      this.renderer.removeClass(this.anchorRef.elementRef.nativeElement, 'anchor-active');
    }
    this.renderer.setStyle(this.document.body, 'overflow', '');
    if (this.enableAnimation) {
      this.transitionState = undefined;
    }

    asyncScheduler.schedule(() => {
      if (!DropdownBase.isAnyOpened) {
        this.renderer.removeClass(this.document.body, 'cdk-overlay');
        this.renderer.removeClass(this.document.body, 'cdk-overlay-off');
      }
      this.closed.next();
    }, 300);
  }

  stopClose(args = { waitForMouseDown: false }): void {
    if (this.innerClickClose) {
      return;
    }
    this.forceStopClose(args);
  }

  forceStopClose(args = { waitForMouseDown: false }): void {
    this.preventClosing = true;
    this.mouseUpSub.unsubscribe();
    if (args.waitForMouseDown) {
      fromWindowEvent('mouseup')
        .pipe(first(), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.mouseUpSub = asyncScheduler.schedule(() => {
            this.preventClosing = false;
          }, 150);
        });
    } else {
      this.mouseUpSub = asyncScheduler.schedule(() => {
        this.preventClosing = false;
      }, 150);
    }
  }

  // close with delay to be able to show some feedback to user
  scheduleClose(args: { closeAll?: boolean; result?: 'OK' | 'CANCEL' | 'DELETE' } = { closeAll: false }): void {
    if (!this.active || this.window.getItem('_dropdown_debug')) {
      return;
    }
    this.result = args.result;
    if (args.closeAll) {
      DropdownBase.dropdownList.forEach(d => {
        if (d !== this && !d.preventClosing) {
          d.cdkOverlay.overlayRef?.overlayElement.firstElementChild?.remove();
          d.close();
        }
      });
    }
    asyncScheduler.schedule(() => {
      this.close();
    }, 100);
  }
}
